import React from 'react';
import stopwords from '../../../utils/stopwords.json';
import './Hit.css';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@asgardeo/auth-react';
import { encodeCaseNumberForPath, normalizeCaseNumbers } from '../../../utils/caseNumber';

const PRESIGNED_BASE_URL = import.meta.env.VITE_PRESIGNED_BASE_URL;

const Hit = ({ hit }) => {
  const navigate = useNavigate();
  const currentPage = useSelector((state) => state.home.currentPage);
  const highlightText = hit._highlightResult.decision_text.value;
  const keywords = hit.keywords || [];
  const legislation = hit.legislation || '';
  const keywordsLawsTerms = hit.keywords_laws_provisions_terms_ || '';
  const snippetText = hit._snippetResult?.decision_text?.value || '';
  const caseNumbers = normalizeCaseNumbers(hit.standard_casenumber);
  const primaryCaseNumber = caseNumbers[0] || '';
  const caseNumberLabel = caseNumbers.join(', ');
  const isSpecialDetermination = caseNumbers.some((caseNumber) =>
    caseNumber.toLowerCase().startsWith('sd')
  );
  const isDigest = caseNumbers.some((caseNumber) =>
    caseNumber.toLowerCase().includes('digest')
  );

  const { state, signIn, getAccessToken } = useAuthContext();
  const selectedOption = useSelector((state) => state.home.selectedOption);
  const searchType = selectedOption;
  const summaryPath = primaryCaseNumber
    ? `/summary/${encodeCaseNumberForPath(primaryCaseNumber)}`
    : null;
  window.dataLayer = window.dataLayer || [];

  const handleViewJudgment = async () => {
    if (!hit.link) return;

    if (!state.isAuthenticated) {
      const currentPath = window.location.pathname;
      const urlString = window.location.search;

      sessionStorage.setItem("redirectToDocument", hit.link);

      if (currentPath === "/search") {
        sessionStorage.removeItem("redirectAfterLogin");
        if (urlString) {
          sessionStorage.setItem("urlString", urlString);
        } else {
          sessionStorage.removeItem("urlString");
        }
      } else if (summaryPath) {
        sessionStorage.setItem("redirectAfterLogin", summaryPath);
      }

      signIn();
      return;
    }

    const newWindow = window.open("about:blank", "_blank");
    try {
      let accessToken = "";
      try {
        accessToken = (await getAccessToken()) || "";
      } catch (tokenError) {
        console.error("Failed to get access token for PDF:", tokenError);
      }
      const response = await fetch(`${PRESIGNED_BASE_URL}/api/pdf/get-pdf-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ link: hit.link }),
      });
      const data = await response.json();
      if (data.success && newWindow) {
        newWindow.location.href = data.presigned_url;
      } else if (newWindow) {
        newWindow.location.href = hit.link;
      }
    } catch {
      if (newWindow) newWindow.location.href = hit.link;
    }
  };

  const handleViewSummary = () => {
    if (!summaryPath) {
      return;
    }

    sessionStorage.setItem(
      "summaryBackPath",
      JSON.stringify({
        summaryPath,
        backPath: `${window.location.pathname}${window.location.search}`,
      })
    );

    if (!state.isAuthenticated) {
      sessionStorage.removeItem("redirectToDocument");
      sessionStorage.removeItem("urlString");
      sessionStorage.setItem("redirectAfterLogin", summaryPath);
      signIn();
      return;
    }

    navigate(summaryPath);
  };

  const formatDate = (date) => {
    const d = new Date(date);
    if (isNaN(d)) {
      console.error('Invalid date:', date);
      return '';
    }
    const day = d.getDate();
    const month = d.toLocaleString('default', { month: 'long' });
    const year = d.getFullYear();

    return `${day} ${month} ${year}`;
  };

  const date = hit.date ? formatDate(hit.date) : '';

  const displayKeywords =
    keywordsLawsTerms.length > 0
      ? keywordsLawsTerms.split(/\s*—\s*/).filter(Boolean)
      : keywords;

  const judges = hit.judge_final || [];
  const judgeText =
    judges.length > 0 && !(judges.length === 1 && judges[0] === 'NA') ? (
      <div className="mt-1 text-14 text-pl-text-secondary">
        <span className="font-normal">
          {judges.length > 1 ? 'Judges:' : 'Judge:'}
        </span>{' '}
        <span className="font-medium">{judges.join(', ')}</span>
      </div>
    ) : null;

  const partiesText =
    hit.nameofparties && hit.nameofparties.trim() !== '' ? (
      <div className="mt-2 text-15 font-medium text-pl-text-primary">
        {isSpecialDetermination
          ? hit.nameofparties
          : isDigest
          ? hit.nameofparties
          : displayNameOfParties(hit.nameofparties)}
      </div>
    ) : null;

  return (
    <article className="flex flex-col bg-pl-bg-elevated border border-pl-border rounded-pl-lg px-[18px] py-20 md:p-[20px_24px] mb-3 hover:shadow-pl-sm hover:border-pl-border-strong transition-all duration-150 ease-in-out">
      <div className="flex items-baseline justify-between">
        <div className="flex items-baseline">
          <button
            type="button"
            onClick={handleViewJudgment}
            className="bg-transparent p-0 border-0 text-left text-15 font-medium font-mono text-pl-accent hover:underline focus:underline focus:outline-none cursor-pointer"
            aria-label={`Open ${caseNumberLabel || 'document'} judgment`}
            title="Open judgment"
          >
            {caseNumberLabel || primaryCaseNumber}
          </button>
          <span className="text-pl-text-tertiary ml-[9px] md:ml-6 text-13">
            {date}
          </span>
        </div>
        {summaryPath && (
          <button
            type="button"
            onClick={handleViewSummary}
            className="w-[68px] md:w-auto flex-none text-left md:text-right leading-4 text-13 text-pl-text-tertiary hover:text-pl-accent transition-colors cursor-pointer"
          >
            View summary &rarr;
          </button>
        )}
      </div>

      {partiesText}
      {judgeText}

      {displayKeywords.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {displayKeywords.map((keyword, i) => (
            <span
              key={i}
              className="bg-pl-bg-subtle border border-pl-border px-2 py-0.5 rounded text-12 text-pl-text-secondary"
            >
              {keyword}
            </span>
          ))}
        </div>
      )}

      {legislation && (
        <div className="mt-2 text-13 text-pl-text-tertiary">
          <span className="mr-1">§</span>
          <span>{legislation.replace(/\s*—\s*/g, ' · ')}</span>
        </div>
      )}

      {currentPage !== 'benchmark' &&
        (searchType === 'Exact Match' ? (
          <div
            className="highlight-text mt-2 text-14 text-pl-text-secondary"
            dangerouslySetInnerHTML={{
              __html: reformatMarkSentences(snippetText, stopwords) || '',
            }}
          ></div>
        ) : (
          <div
            className="highlight-text mt-2 text-14 text-pl-text-secondary"
            dangerouslySetInnerHTML={{
              __html: reformatMarkSentences(highlightText, stopwords) || '',
            }}
          ></div>
        ))}
    </article>
  );
};

function displayNameOfParties(nameOfParties) {
  let textToDisplay = '';

  if (nameOfParties && nameOfParties.length > 0) {
    if (nameOfParties.length <= 250) {
      return nameOfParties;
    }

    const vsIndex = nameOfParties.search(/vs./i);

    if (vsIndex > 0) {
      const beforeVs = nameOfParties.substring(0, Math.min(vsIndex, 50)).trim();
      const afterVs = nameOfParties.substring(vsIndex + 3, vsIndex + 53).trim();
      textToDisplay = `${beforeVs} . . . Vs. ${afterVs} . . .`;
    } else {
      textToDisplay = nameOfParties.substring(0, 100);
    }
  }

  if (textToDisplay.match(/^[^a-zA-Z]/)) {
    textToDisplay = textToDisplay.replace(/^[^a-zA-Z]+/, '');
  }

  return textToDisplay;
}

function reformatMarkSentences(text, stopwords) {
  let sentencesWithMark = getSentencesWithMark(text);
  let reformattedSentences = sentencesWithMark.map((sentence) =>
    removeMarkIfStopword(sentence, stopwords)
  );

  let sentenceMarkCount = reformattedSentences.map((sentence) => {
    let markMatches = sentence.match(/<mark>(.*?)<\/mark>/g) || [];
    let uniqueWords = new Set(
      markMatches.map((match) =>
        match.replace('<mark>', '').replace('</mark>', '').toLowerCase()
      )
    );

    return { sentence, markCount: uniqueWords.size };
  });

  sentenceMarkCount.sort((a, b) => b.markCount - a.markCount);
  let reformatted = sentenceMarkCount.slice(0, 5).map((item) => item.sentence);

  return getSentencesWithMark(reformatted.join('. ')).join(' . . .');
}

function getSentencesWithMark(text) {
  let sentences = text.split('.');
  let sentencesWithMark = sentences.filter((sentence) =>
    sentence.includes('<mark>')
  );
  return sentencesWithMark;
}

function removeMarkIfStopword(markSentence, stopwords) {
  let markMatches = markSentence.match(/<mark>(.*?)<\/mark>/g);
  if (!markMatches) {
    return markSentence;
  }

  let markWords = markMatches.map((word) =>
    word.replace('<mark>', '').replace('</mark>', '')
  );

  for (let word of markWords) {
    if (stopwords.includes(word.trim().toLowerCase())) {
      markSentence = markSentence.replace(`<mark>${word}</mark>`, word);
    }
  }
  return markSentence;
}

export default Hit;
